'use client';

import {
  autoUpdate,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { type ChangeEvent, useState } from 'react';

import { CodeSnippet } from '@/components/CodeSnippet';
import { Select } from '@/components/FormElements';
import { useFlags } from '@/hooks/useFlags';
import Flag from '@/icons/react/Flag';
import X from '@/icons/react/X';
import { getConfig } from '@/lib/flags';

export type FeatureFlagsProps = Record<string, never>;

interface IFlagProps {
  flags: Record<string, string>;
}

function Flags({ flags }: IFlagProps) {
  const { ID, cohort, updateFlags } = useFlags();

  function handleCheckboxChange(event: ChangeEvent<HTMLSelectElement>) {
    const newFlags = { ...flags };
    newFlags[event.target.name] = event.target.value;
    updateFlags(ID, newFlags);
  }

  return (
    <>
      <ul className="flex flex-col gap-4 p-5 w-full overflow-y-auto">
        {Object.entries(flags).map(([name, _]) => {
          const cfg = getConfig()[name];

          if (!cfg?.variants?.length) return null;

          return (
            <li key={name}>
              <Select
                name={name}
                id="variant"
                displaySize="md"
                label={cfg.title || name}
                onChange={handleCheckboxChange}
                defaultValue={`${flags[name]}`}
                options={cfg.variants.map((variant) => {
                  return {
                    value: variant.toString(),
                    label: variant.toString(),
                  };
                })}
              />
            </li>
          );
        })}
      </ul>
      <CodeSnippet className="text-body-300 bg-gray-100 w-full border-t border-gray-300 px-5 py-5 rounded-b-3">
        Cohort: {cohort}
      </CodeSnippet>
    </>
  );
}

export function FeatureFlags(_: FeatureFlagsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { flags, showFlagsUI } = useFlags();
  const { refs, context, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(8)],
    placement: 'left',
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  function handleClick() {
    setIsOpen(!open);
  }

  if (!showFlagsUI) return null;

  return (
    <>
      <button
        onClick={handleClick}
        ref={refs.setReference}
        {...getReferenceProps()}
        title="Feature Flags"
        className="fixed top-1/2 right-4 -translate-y-1/2 rounded-full w-10 h-10 bg-black text-paper shadow-lg flex items-center justify-center hover:bg-gray-700 transition-colors border border-paper"
      >
        <Flag />
      </button>

      {isOpen && (
        <FloatingPortal id="floating-ui-portal">
          <FloatingOverlay className="bg-black/50 flex items-end justify-center z-10 md:p-4 md:items-center">
            <FloatingFocusManager context={context}>
              <div
                ref={refs.setFloating}
                {...getFloatingProps()}
                className="bg-paper border border-primary rounded-3 shadow-xl md:min-w-[40rem] max-h-dvh flex flex-col"
                style={floatingStyles}
              >
                <div className="flex items-center justify-between gap-4 w-full p-5 border-b border-gray-300">
                  <h2 className="text-heading-500">Feature Flags</h2>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="items-center text-gray-500 text-body-350 flex gap-1 hover:text-gray-700"
                  >
                    Close
                    <X className="translate-y-px" />
                  </button>
                </div>
                <Flags flags={flags} />
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </>
  );
}
